import { useId } from '@reach/auto-id';
import { useEffect, useRef, useState } from 'react';

import { mergeRefs } from '../utils';

import { getFixedAttr, usePopper } from './usePopper';

export enum EChildMenuDirections {
  Right = 'Right',
  Left = 'Left'
}

export const useControls = (props) => {
  const {
    context,
    placement: placementProp = 'bottom-start',
    childDirection: childDirection = EChildMenuDirections.Right,
    onChange,
    selectedValue,
    flip = false,
    fixed,
    width
  } = props;

  // If this menu is a nested menu, that means there's a parent menu context
  const parentMenu = context;

  // Check if this menu is a nested menu or top level menu
  const hasParentMenu = Boolean(parentMenu);

  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isOpen, setIsOpen] = useState(false);

  const menuId = `select-${useId()}`;
  const buttonId = `selectbutton-${useId()}`;

  const focusableItems = useRef(null);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // turn the childDirection prop into a popper-friendly prop
  const childDirectionProp = childDirection === EChildMenuDirections.Left ? 'left-start' : 'right-start';

  const { placement, popper, reference } = usePopper({
    placement: !hasParentMenu ? placementProp : childDirectionProp,
    forceUpdate: isOpen,
    preventOverflow: true,
    flip,
    offset: !hasParentMenu ? [0, 4] : [0, 0],
    fixed: getFixedAttr(fixed, width)
  });

  useEffect(() => {
    if (isOpen && menuRef && menuRef.current) {
      const focusables = Array.from(menuRef.current.querySelectorAll('[role="option"]'));
      focusableItems.current = menuRef.current ? focusables : [];
      initTabIndex();
    }
  }, [isOpen, menuRef]);

  const onOpen = () => setIsOpen(true);
  const onClose = () => {
    setFocusedIndex(-1);
    setIsOpen(false);
    if (process.env.NODE_ENV === 'test') {
      // Not wrapping this call in setTimeout 0 causes a "Maximum call stack size exceeded" error in tests.
      // See https://github.com/testing-library/react-testing-library/issues/627
      setTimeout(() => {
        buttonRef.current?.focus();
      }, 0);
    } else {
      buttonRef.current?.focus();
    }
  };

  const onCloseAll = () => {
    onClose();

    let next = parentMenu;
    while (next != null) {
      next.onClose();
      next = next.parentMenu;
    }
  };

  const openAndFocusMenu = () => {
    onOpen();
    if (menuRef.current) {
      menuRef.current.focus();
    }
  };

  const updateTabIndex = (index) => {
    if (focusableItems.current.length > 0) {
      const nodeAtIndex = focusableItems.current[index];
      focusableItems.current.forEach((node) => {
        if (node !== nodeAtIndex) {
          node.setAttribute('tabindex', -1);
        }
      });
      nodeAtIndex.setAttribute('tabindex', 0);
    }
  };

  const initTabIndex = () => {
    focusableItems.current.forEach((node, index) => index === 0 && node.setAttribute('tabindex', 0));
  };

  const findSelectedOptionIndex = () => {
    return focusableItems.current.findIndex((node) => node.getAttribute('aria-selected') === 'true');
  };

  useEffect(() => {
    if (focusedIndex !== -1 && isOpen) {
      const selectedIndex = findSelectedOptionIndex() >= 0 ? findSelectedOptionIndex() : 0;
      //the index of the selected option could change if another component changes the collection length
      //this fix issue in https://github.com/OpenGov/voltron/pull/12333
      if (!!focusableItems.current) {
        updateTabIndex(selectedIndex);
        if (focusableItems.current[focusedIndex]) {
          focusableItems.current[focusedIndex].focus();
          return;
        }
        focusableItems.current[0].focus();
      }
    }
    if (focusedIndex === -1 && isOpen) {
      const selectedIndex = findSelectedOptionIndex();
      if (selectedIndex === -1) {
        setFocusedIndex(0);
      } else {
        setFocusedIndex(selectedIndex);
      }
    }
  }, [focusedIndex, isOpen, buttonRef, menuRef]);

  const handleClick = (e) => {
    if (menuRef.current.contains(e.target)) {
      return;
    }

    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [isOpen]);

  return {
    popper,
    reference,
    focusedIndex,
    setFocusedIndex,
    openAndFocusMenu,
    isOpen,
    onOpen,
    onClose,
    onCloseAll,
    buttonRef: mergeRefs(buttonRef, reference.ref),
    menuRef: mergeRefs(menuRef, popper.ref),
    focusableItems,
    placement,
    childDirection,
    menuId,
    buttonId,
    closeOnBlur: true,
    parentMenu,
    hasParentMenu,
    onChange,
    selectedValue
  };
};
